import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useApp } from '../../context/AppContext.tsx';
import { buildWaUrl, formatPhoneTel, formatWaNumber, parseGoogleMapsEmbedUrl } from '../../lib/contactUtils.ts';
import {
  Phone,
  Mail,
  MapPin,
  Send,
  MessageCircle,
  MessageSquare,
  Clock,
  CheckCircle2,
  Copy,
  ArrowLeft,
  ExternalLink,
} from 'lucide-react';

export default function ContactPage() {
  const { settings, showToast, copyPageUrl, navigate } = useApp();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('Pemesanan Paket Outbound');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const contactInfo = settings?.contact;
  const contactStatic = settings?.staticPages?.contactUs;
  const primaryColor = settings?.theme.primaryColor || '#059669';

  useEffect(() => {
    const customTab = settings?.pageTabTitles?.contact?.trim();
    if (customTab) {
      document.title = customTab;
    } else {
      const pageTitle = contactStatic?.metaTitle || contactStatic?.title || 'Hubungi Kami';
      const suffix = settings?.pageTabTitles?.titleSuffix !== undefined ? settings.pageTabTitles.titleSuffix : ` | ${settings?.siteName || ''}`;
      document.title = `${pageTitle}${suffix}`;
    }
  }, [contactStatic?.title, contactStatic?.metaTitle, settings?.siteName, settings?.pageTabTitles?.contact, settings?.pageTabTitles?.titleSuffix]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !message) {
      showToast('Mohon isi nama dan pesan Anda', 'error');
      return;
    }

    const waNumber = (contactInfo?.whatsappNumber || '6281234567890').replace(/[^0-9]/g, '');
    const waText = `*Pesan Baru dari Form Kontak Website:*
- Nama: ${name}
- Email: ${email || '-'}
- No. HP: ${phone || '-'}
- Topik: ${subject}
- Pesan: ${message}`;

    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(waText)}`, '_blank');
    setSent(true);
    showToast('Pesan Anda siap dikirimkan ke WhatsApp Admin!', 'success');
  };

  return (
    <div id="contact-page-view" className="py-10 sm:py-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Navigation & Action Header */}
        <div className="flex items-center justify-between gap-4 mb-8">
          <button
            onClick={() => navigate('home')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Beranda</span>
          </button>

          <button
            onClick={() => copyPageUrl('/contact', 'Hubungi Kami')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition-all shadow-2xs border border-slate-200"
            title="Salin Tautan Halaman Ini"
          >
            <Copy className="w-3.5 h-3.5 text-slate-500" />
            <span>Salin URL (/contact)</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold mb-3 border border-emerald-200">
            <MessageSquare className="w-3.5 h-3.5" />
            <span>{contactStatic?.badge || 'Konsultasi & Reservasi 24/7'}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            {contactStatic?.title || 'Hubungi Kami'}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-2 leading-relaxed">
            {contactStatic?.subtitle ||
              'Konsultasikan kebutuhan acara, kemitraan, atau pemesanan langsung dengan tim responsif kami'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Direct Info & Markdown */}
          <div className="lg:col-span-5 space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-xs space-y-6">
              <h3 className="font-bold text-lg text-slate-900 pb-3 border-b border-slate-100">
                Informasi Kontak Resmi
              </h3>

              {contactInfo?.address && (
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0 mt-0.5"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-400 font-bold uppercase block">
                      Alamat Kantor
                    </span>
                    <p className="text-xs text-slate-700 font-medium leading-relaxed mt-0.5">
                      {contactInfo.address}
                    </p>
                  </div>
                </div>
              )}

              {contactInfo?.whatsappNumber && (
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <MessageCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-400 font-bold uppercase block">
                      WhatsApp Fast Response
                    </span>
                    <a
                      href={buildWaUrl(contactInfo.whatsappNumber)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-700 font-bold hover:underline inline-flex items-center gap-1 mt-0.5"
                    >
                      <span>+{formatWaNumber(contactInfo.whatsappNumber)}</span>
                      <ExternalLink className="w-3 h-3 text-emerald-600" />
                    </a>
                  </div>
                </div>
              )}

              {contactInfo?.phone && (
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-400 font-bold uppercase block">
                      Telepon Kantor / HP Resmi
                    </span>
                    <a
                      href={`tel:${formatPhoneTel(contactInfo.phone)}`}
                      className="text-xs text-slate-800 font-bold hover:underline inline-flex items-center gap-1 mt-0.5"
                    >
                      <span>{contactInfo.phone}</span>
                    </a>
                  </div>
                </div>
              )}

              {contactInfo?.email && (
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-400 font-bold uppercase block">
                      Email Resmi
                    </span>
                    <p className="text-xs text-slate-700 font-medium mt-0.5">
                      {contactInfo.email}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0"
                  style={{ backgroundColor: primaryColor }}
                >
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <span className="text-[11px] text-slate-400 font-bold uppercase block">
                    Jam Layanan Pelanggan
                  </span>
                  <p className="text-xs text-slate-700 font-medium mt-0.5">
                    Senin - Minggu: 08.00 - 21.00 WIB
                  </p>
                </div>
              </div>
            </div>

            {/* Markdown Extra Info (Only show if customized without hardcoded old numbers) */}
            {contactStatic?.content && !contactStatic.content.includes('+62821-3095-5059') && (
              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 text-xs text-slate-600">
                <ReactMarkdown>{contactStatic.content}</ReactMarkdown>
              </div>
            )}
          </div>

          {/* Right Column: Interactive Contact Form & Map */}
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-xs">
              <h3 className="font-bold text-lg text-slate-900 mb-2">Kirim Pesan Langsung</h3>
              <p className="text-xs text-slate-500 mb-6">
                Isi formulir di bawah untuk mendapatkan respon instan via WhatsApp dari tim kami.
              </p>

              {sent ? (
                <div className="p-6 bg-emerald-50 rounded-2xl border border-emerald-200 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                  <h4 className="font-bold text-sm text-emerald-900">Pesan Telah Diteruskan!</h4>
                  <p className="text-xs text-emerald-700 mt-1">
                    Terima kasih telah menghubungi kami. Tim kami akan segera menanggapi konsultasi Anda.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-4 px-4 py-2 bg-emerald-700 text-white text-xs font-bold rounded-lg"
                  >
                    Kirim Pesan Lain
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Nama Lengkap *</label>
                      <input
                        type="text"
                        required
                        placeholder="Contoh: Andi Pratama"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Nomor WhatsApp / HP *</label>
                      <input
                        type="tel"
                        required
                        placeholder="0812-xxxx-xxxx"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Email (Opsional)</label>
                      <input
                        type="email"
                        placeholder="andi@gmail.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-700 block mb-1">Kategori Kebutuhan</label>
                      <select
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                      >
                        <option>Pemesanan Paket Outbound</option>
                        <option>Offroad & Rafting Adventure</option>
                        <option>Pembelian Produk Kopi & UMKM</option>
                        <option>Jasa Website & Digital Agency</option>
                        <option>Kerjasama Kemitraan & Sponsorship</option>
                        <option>Lainnya</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-slate-700 block mb-1">Detail Rencana / Pesan *</label>
                    <textarea
                      required
                      rows={4}
                      placeholder="Jelaskan perkiraan tanggal acara, jumlah peserta, atau produk yang diminati..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-3.5 py-2.5 text-xs bg-slate-50 border border-slate-300 rounded-xl focus:outline-hidden focus:ring-2 focus:ring-emerald-500 text-slate-800"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-xl text-white font-bold text-xs shadow-md hover:opacity-95 transition-all flex items-center justify-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Send className="w-4 h-4" />
                    <span>Kirim Pesan ke Tim Reservasi</span>
                  </button>
                </form>
              )}
            </div>

            {/* Google Maps Embed with fallbacks */}
            <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-xs h-64 bg-slate-100">
              <iframe
                src={parseGoogleMapsEmbedUrl(contactStatic?.mapEmbedUrl, contactInfo?.address)}
                title="Lokasi Peta Kantor"
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
